import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AudioAI } from "../../types/types";
import { useGlobalState } from "../../context/useGlobalState";
import {
    getDrafts,
    deleteDraftItem,
    updateDraftItem,
    createEmptyProject,
    updateAudioAIName,
    getVoices,
} from "../../api";
import TextInput from "../../components/ui/TextInput";
import AudioPlayerRewind from "../../components/common/AudioPlayer";
import Icon from "../../components/common/Icon";
import Dropdown from "../../components/common/Dropdown";
import TextModal from "../../components/modals/ProjectTextModal";
import DeleteModal from "../../components/modals/DeleteModal";
import AddToProjectModal from "../../components/modals/AddToProjectModal";
import Pagination from "../../components/common/Pagination";
import s from "../../components/common/ContentSurface.module.css";
import useCompositionStates from "../../hooks/useCompositionStates";
import audioFooter from "../../components/common/AudioPlayerFooter.module.css";

interface VoiceLookupItem {
    id: string;
    name: string;
}

type ContentFilter = "all" | "text" | "audio";

const PAGE_SIZE = 100;

const findVoiceName = (audio: AudioAI, byId: Map<string, VoiceLookupItem>, languagesAI: any[], t: any) => {
    const rawAudio = audio as any;
    // The language of the content comes first: a text translated into Polish stays Polish even when
    // the voice reading it is declared Portuguese. The voice only answers for audios without one.
    const languageCode = audio.language || audio.voiceAI?.language || "";
    const languageLabel = languagesAI?.find((lang) => lang.code === languageCode)?.label || languageCode;

    // Multi-voice dialog: aggregate the distinct voices from the dialog lines
    // (same behaviour as the project view, instead of showing only the primary voice).
    if (rawAudio.isDialog && Array.isArray(rawAudio.dialogLines) && rawAudio.dialogLines.length > 0) {
        const names: string[] = [];
        const seen = new Set<string>();
        for (const line of rawAudio.dialogLines) {
            const lineVoice =
                (line.voiceAIId && byId.get(line.voiceAIId)) ||
                (line.voiceCloneId && byId.get(line.voiceCloneId)) ||
                (line.voicePromptId && byId.get(line.voicePromptId));
            if (lineVoice && !seen.has(lineVoice.name)) {
                seen.add(lineVoice.name);
                names.push(lineVoice.name);
            }
        }
        if (names.length > 0) {
            return `${t("recordedBy")} ${names.join(", ")} ${t("inLang")} ${languageLabel}`;
        }
    }

    const voice =
        (audio.voiceAIId && byId.get(audio.voiceAIId)) ||
        (rawAudio.voiceCloneId && byId.get(rawAudio.voiceCloneId)) ||
        (rawAudio.voicePromptId && byId.get(rawAudio.voicePromptId));
    if (voice) return `${t("recordedBy")} ${voice.name} ${t("inLang")} ${languageLabel}`;
    if (audio.voiceAI) return `${t("recordedBy")} ${audio.voiceAI.name} ${t("inLang")} ${languageLabel}`;
    return audio.audioUrl ? t("noInformationAvailable") : t("noAudioAvailable");
};

const AudioDrafts: React.FC = () => {
    const navigate = useNavigate();
    const token = localStorage.getItem("token");
    const { data } = useGlobalState();
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const [drafts, setDrafts] = useState<AudioAI[]>([]);
    const [page, setPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [contentFilter, setContentFilter] = useState<ContentFilter>("all");
    const [search, setSearch] = useState<string>("");
    const [debouncedSearch, setDebouncedSearch] = useState<string>("");

    const [selectedVoice, setSelectedVoice] = useState<AudioAI | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [showAudioPlayer, setShowAudioPlayer] = useState<boolean>(false);
    const [selectedTextIds, setSelectedTextIds] = useState<Set<string>>(new Set());
    const [voiceLookupById, setVoiceLookupById] = useState<Map<string, VoiceLookupItem>>(new Map());

    const [editingAudioId, setEditingAudioId] = useState<string | null>(null);
    const [editedAudioName, setEditedAudioName] = useState<string>("");
    const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
    const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState<boolean>(false);
    // Draft currently being assigned to a project (opens the project picker modal).
    const [addTargetId, setAddTargetId] = useState<string | null>(null);

    const withText = contentFilter !== "audio";
    const withAudio = contentFilter !== "text";

    // Debounce the search box.
    useEffect(() => {
        const id = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1);
        }, 350);
        return () => clearTimeout(id);
    }, [search]);

    const fetchDrafts = async () => {
        if (!token) return;
        try {
            const response = await getDrafts({
                token,
                itemType: "audio",
                page,
                limit: PAGE_SIZE,
                language: selectedContentLanguage,
                withText,
                withAudio,
                query: debouncedSearch || undefined,
            });
            setDrafts((response.items as AudioAI[]) || []);
            setTotalPages(response.totalPages || 1);
            setTotalCount(response.totalCount || 0);
        } catch (error) {
            console.error("Error fetching audio drafts:", error);
        }
    };

    // Filters and search are applied server-side so pagination/total stay consistent.
    useEffect(() => {
        fetchDrafts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, selectedContentLanguage, contentFilter, debouncedSearch]);

    useEffect(() => {
        const fetchVoicesForLookup = async () => {
            if (!token) return;
            try {
                const voices = await getVoices({ token, language: "EN" });
                const lookup = new Map<string, VoiceLookupItem>();
                voices.forEach((voice) => {
                    const item: VoiceLookupItem = { id: voice.id, name: voice.name };
                    lookup.set(voice.id, item);
                    if (voice.voiceCloneDbId) lookup.set(voice.voiceCloneDbId, item);
                    if (voice.promptVoiceDbId) lookup.set(voice.promptVoiceDbId, item);
                });
                setVoiceLookupById(lookup);
            } catch (error) {
                console.error("Error fetching voices for lookup:", error);
            }
        };
        fetchVoicesForLookup();
    }, [token]);

    const filtered = drafts;
    // All rows are selectable (for bulk delete); TTS only applies to those with text.
    const selectableAudios = filtered;
    const allSelected = selectableAudios.length > 0 && selectableAudios.every((a) => selectedTextIds.has(a.id));
    const selectedTextCount = filtered.filter((a) => selectedTextIds.has(a.id) && a.text).length;
    // The editor works on a single audio at a time.
    const selectedAudioCount = filtered.filter((a) => selectedTextIds.has(a.id) && a.audioUrl).length;

    // Which rows are dressed and which could be. Derived server-side from the
    // compositions, so it stays right without a status column to maintain.
    const audioIdsForStates = useMemo(() => filtered.map((audio) => audio.id), [filtered]);
    const compositionStates = useCompositionStates(audioIdsForStates);

    const toggleSelected = (audioId: string) => {
        setSelectedTextIds((prev) => {
            const next = new Set(prev);
            if (next.has(audioId)) next.delete(audioId);
            else next.add(audioId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        setSelectedTextIds(() => (allSelected ? new Set<string>() : new Set(selectableAudios.map((a) => a.id))));
    };

    const handleSendToTextToSpeech = () => {
        const orderedSelected = filtered.filter((a) => selectedTextIds.has(a.id) && a.text);
        if (orderedSelected.length === 0) return;
        const importedCapsules = orderedSelected.map((a) => ({
            name: a.name || "",
            text: a.text || "",
            language: a.language || null,
        }));
        navigate("/create/text-to-speech", { state: { importedCapsules, projectName: t("drafts") || "" } });
    };

    // One text becomes the source of a new remix. The remix queue takes a single text,
    // so the action is offered for one text at a time, like the audio editor.
    const handleSendToTextRemix = () => {
        const selected = filtered.filter((a) => selectedTextIds.has(a.id) && a.text);
        if (selected.length !== 1) return;
        const [audio] = selected;
        navigate("/create/text-remix", {
            state: {
                remixSource: { name: audio.name || "", text: audio.text || "", language: audio.language || null },
            },
        });
    };

    // Open one audio in the editor. It loads with its own sound design, or the one of the
    // content it was translated from, resolved when the editor opens. The editor renders a
    // single mix, so the action is offered for one audio at a time.
    const handleSendToAudioEditing = () => {
        const selected = filtered.filter((a) => selectedTextIds.has(a.id) && a.audioUrl);
        if (selected.length !== 1) return;
        navigate("/create/audio-editing", { state: { audioAIId: selected[0].id } });
    };

    const handleAudioNameChange = async (audioId: string) => {
        if (!token) return;
        try {
            await updateAudioAIName({ token, audioAIId: audioId, name: editedAudioName });
            setEditingAudioId(null);
            await fetchDrafts();
        } catch (error) {
            console.error("Failed to update audio name:", error);
            setEditingAudioId(null);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!token || !deleteTargetId) return;
        try {
            await deleteDraftItem({ token, itemId: deleteTargetId, itemType: "audio" });
            setDeleteTargetId(null);
            if (drafts.length === 1 && page > 1) setPage((p) => p - 1);
            else await fetchDrafts();
        } catch (error) {
            console.error("Failed to delete draft:", error);
            setDeleteTargetId(null);
        }
    };

    // Assign a draft to a project (existing or newly created). The draft then leaves
    // the brouillons list (it now has a projectAIId).
    const handleAddToProject = async (projectId: string, newProjectName: string) => {
        if (!token || !addTargetId) return;
        try {
            let targetProjectAIId = projectId;
            if (projectId === "new") {
                if (!newProjectName.trim()) return;
                const created = await createEmptyProject({ token, name: newProjectName.trim() });
                if (!created?.id) return;
                targetProjectAIId = created.id;
            }
            await updateDraftItem({ token, itemId: addTargetId, itemType: "audio", targetProjectAIId });
            setAddTargetId(null);
            if (drafts.length === 1 && page > 1) setPage((p) => p - 1);
            else await fetchDrafts();
        } catch (error) {
            console.error("Failed to add draft to project:", error);
            setAddTargetId(null);
        }
    };

    const handleBulkDeleteConfirm = async () => {
        if (!token || selectedTextIds.size === 0) return;
        const ids = Array.from(selectedTextIds);
        try {
            await Promise.all(ids.map((itemId) => deleteDraftItem({ token, itemId, itemType: "audio" })));
            setSelectedTextIds(new Set());
            setIsBulkDeleteOpen(false);
            if (drafts.length <= ids.length && page > 1) setPage((p) => p - 1);
            else await fetchDrafts();
        } catch (error) {
            console.error("Failed to bulk delete drafts:", error);
            setIsBulkDeleteOpen(false);
        }
    };

    return (
        <div className={s.page}>
            <div className={s.header}>
                <div className={s.headerLeft}>
                    <div>
                        <h1 className={s.title}>
                            {t("draftsAudioTitle")} ({totalCount})
                        </h1>
                        <p className={s.subtitle}>{t("draftsAudioSubtitle")}</p>
                    </div>
                </div>
            </div>

            <div className={s.divider} />

            <div className={s.toolbar}>
                <div className={s.search}>
                    <Icon name="ic-search" className={s.searchIcon} />
                    <input
                        className={s.searchInput}
                        type="text"
                        placeholder={t("searchByTitleVoiceLanguage")}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div style={{ minWidth: 180 }}>
                    <Dropdown
                        label=""
                        value={selectedContentLanguage}
                        options={data?.languagesAI?.map((l: { code: string; label: string }) => ({
                            value: l.code,
                            label: l.label,
                        }))}
                        onChange={(language: string | null) => {
                            setSelectedContentLanguage(language);
                            setPage(1);
                        }}
                        placeholder={t("selectALanguage")}
                        loading={false}
                        error={false}
                    />
                </div>
                <div className={s.pills}>
                    {(["all", "text", "audio"] as ContentFilter[]).map((f) => (
                        <button
                            key={f}
                            type="button"
                            className={`${s.pill} ${contentFilter === f ? s.pillActive : ""}`}
                            onClick={() => {
                                setContentFilter(f);
                                setPage(1);
                            }}
                        >
                            {f === "all" ? t("filterAll") : f === "text" ? t("filterWithText") : t("filterWithAudio")}
                        </button>
                    ))}
                </div>
            </div>

            {selectableAudios.length > 0 && (
                <div className={s.selectAllRow}>
                    <label className={s.selectAllLabel}>
                        <input
                            type="checkbox"
                            className={s.checkbox}
                            checked={allSelected}
                            onChange={toggleSelectAll}
                        />
                        {t("selectAll")}
                    </label>
                    {selectedTextIds.size > 0 && (
                        <div className={s.selectActions}>
                            {selectedTextCount > 0 && (
                                <button type="button" className={s.ttsBtn} onClick={handleSendToTextToSpeech}>
                                    {t("textToSpeech")} ({selectedTextCount})
                                </button>
                            )}
                            {selectedTextCount > 0 && (
                                <button
                                    type="button"
                                    className={s.editBtn}
                                    title={
                                        selectedTextCount > 1
                                            ? t("textRemixSelectOnlyOne") || ""
                                            : t("sendSelectedToTextRemixTooltip") || ""
                                    }
                                    onClick={handleSendToTextRemix}
                                    disabled={selectedTextCount > 1}
                                >
                                    <Icon name="ic-shuffle" className={s.editBtnIcon} />
                                    {t("textRemix")} ({selectedTextCount})
                                </button>
                            )}
                            {selectedAudioCount > 0 && (
                                <button
                                    type="button"
                                    className={s.editBtn}
                                    title={
                                        selectedAudioCount > 1
                                            ? t("audioEditingSelectOnlyOne") || ""
                                            : t("sendSelectedToAudioEditingTooltip") || ""
                                    }
                                    onClick={handleSendToAudioEditing}
                                    disabled={selectedAudioCount > 1}
                                >
                                    <Icon name="ic-scissors" className={s.editBtnIcon} />
                                    {t("audioEditing")} ({selectedAudioCount})
                                </button>
                            )}
                            <button type="button" className={s.bulkDeleteBtn} onClick={() => setIsBulkDeleteOpen(true)}>
                                {t("deleteSelected")} ({selectedTextIds.size})
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div className={`${selectedVoice?.audioUrl && showAudioPlayer ? "with-player-margin" : ""}`}>
                <div className={s.list}>
                    {filtered.map((audio) => {
                        const langCode = audio.language || audio.voiceAI?.language || "";
                        return (
                            <div key={audio.id} className={s.row}>
                                <div className={s.rowLeft}>
                                    <input
                                        type="checkbox"
                                        className={s.checkbox}
                                        checked={selectedTextIds.has(audio.id)}
                                        onChange={() => toggleSelected(audio.id)}
                                        title={t("select")}
                                    />
                                    {audio.audioUrl ? (
                                        <div
                                            className={s.playBtn}
                                            onClick={() => {
                                                setSelectedVoice(audio);
                                                setShowAudioPlayer(true);
                                            }}
                                        >
                                            <Icon name="ic-play" className={s.playIcon} />
                                        </div>
                                    ) : (
                                        <div className={`${s.playBtn} ${s.playBtnDisabled}`}>
                                            <Icon name="ic-play" className={s.playIcon} />
                                        </div>
                                    )}
                                    <div className={s.info}>
                                        <div className={s.nameRow}>
                                            {editingAudioId === audio.id ? (
                                                <TextInput
                                                    placeholder={t("nameOfTheNewProject")}
                                                    value={editedAudioName}
                                                    size="small"
                                                    onChange={(e) => setEditedAudioName(e.target.value)}
                                                    onKeyPress={(e) =>
                                                        e.key === "Enter" && handleAudioNameChange(audio.id)
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    <h4
                                                        className={s.name}
                                                        onClick={() =>
                                                            navigate(`/create/drafts/update-item/${audio.id}`, {
                                                                state: { item: audio },
                                                            })
                                                        }
                                                    >
                                                        {audio.name}
                                                    </h4>
                                                    <Icon
                                                        name="ic-pencil"
                                                        className={s.editIcon}
                                                        onClick={() =>
                                                            navigate(`/create/drafts/update-item/${audio.id}`, {
                                                                state: { item: audio },
                                                            })
                                                        }
                                                    />
                                                </>
                                            )}
                                        </div>
                                        <p className={s.meta}>
                                            {findVoiceName(audio, voiceLookupById, data?.languagesAI || [], t)}
                                        </p>
                                    </div>
                                </div>

                                <div className={s.rowRight}>
                                    {compositionStates[audio.id] === "available" && (
                                        <button
                                            type="button"
                                            className={s.dressBadge}
                                            title={t("soundDesignToApplyTooltip") || ""}
                                            onClick={() =>
                                                navigate("/create/audio-editing", {
                                                    state: { audioAIId: audio.id },
                                                })
                                            }
                                        >
                                            <Icon name="ic-music" className={s.dressBadgeIcon} />
                                            {t("soundDesignToApply")}
                                        </button>
                                    )}
                                    {compositionStates[audio.id] === "dressed" && (
                                        <span className={s.dressedBadge} title={t("soundDesignAppliedTooltip") || ""}>
                                            <Icon name="ic-check" className={s.dressBadgeIcon} />
                                            {t("soundDesignApplied")}
                                        </span>
                                    )}
                                    {langCode && <span className={s.lang}>{langCode.toUpperCase()}</span>}
                                    {audio.text && (
                                        <Icon
                                            name="ic-file-text"
                                            className={s.iconAction}
                                            onClick={() => {
                                                if (selectedVoice && selectedVoice.id !== audio.id) {
                                                    setShowAudioPlayer(false);
                                                }
                                                setSelectedVoice(audio);
                                                setIsModalOpen(true);
                                            }}
                                        />
                                    )}
                                    {audio.audioUrl && (
                                        <Icon
                                            name="ic-download"
                                            className={s.iconAction}
                                            onClick={async () => {
                                                try {
                                                    const response = await fetch(audio.audioUrl, { cache: "reload" });
                                                    const blob = await response.blob();
                                                    const url = URL.createObjectURL(blob);
                                                    const link = document.createElement("a");
                                                    link.href = url;
                                                    link.setAttribute("download", `${audio.name}.mp3`);
                                                    document.body.appendChild(link);
                                                    link.click();
                                                    document.body.removeChild(link);
                                                    URL.revokeObjectURL(url);
                                                } catch (error) {
                                                    console.error("Error downloading audio:", error);
                                                }
                                            }}
                                        />
                                    )}
                                    <Icon
                                        name="ic-folder"
                                        className={s.iconAction}
                                        onClick={() => setAddTargetId(audio.id)}
                                    />
                                    <button
                                        type="button"
                                        className={s.removeBtn}
                                        onClick={() => setDeleteTargetId(audio.id)}
                                    >
                                        {t("remove")}
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                    {filtered.length === 0 && <div className={s.emptyState}>{t("noInformationAvailable")}</div>}
                </div>
            </div>

            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

            {selectedVoice?.audioUrl && showAudioPlayer && (
                <div className={audioFooter.section}>
                    <AudioPlayerRewind
                        title={selectedVoice.name}
                        src={selectedVoice.audioUrl}
                        isAutoPlay={true}
                        onClickAddToProject={() => {}}
                        showAddtoProject={false}
                    />
                </div>
            )}

            {isModalOpen && selectedVoice && (
                <TextModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={selectedVoice.name}
                    content={selectedVoice.text || ""}
                />
            )}

            <DeleteModal
                isOpen={deleteTargetId !== null}
                onClose={() => setDeleteTargetId(null)}
                onConfirm={handleDeleteConfirm}
                message={t("deleteDraftConfirmation")}
                title={t("deleteDraft")}
            />

            <DeleteModal
                isOpen={isBulkDeleteOpen}
                onClose={() => setIsBulkDeleteOpen(false)}
                onConfirm={handleBulkDeleteConfirm}
                message={t("deleteSelectedConfirmation", { count: selectedTextIds.size })}
                title={t("deleteSelected")}
            />

            <AddToProjectModal
                isOpen={addTargetId !== null}
                onClose={() => setAddTargetId(null)}
                onAddToProject={handleAddToProject}
                placeholder={t("addToProject")}
            />
        </div>
    );
};

export default AudioDrafts;
